import React, {Fragment, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react'
import {Box, Button, Chip, Slide, Stack, styled, Typography, useTheme} from '@mui/material'
import {
    GeolocateControl,
    Layer,
    Map,
    Marker,
    NavigationControl,
    Popup as PopupComponent,
    ScaleControl,
    Source,
    useMap
} from '@vis.gl/react-maplibre'
import {Popup} from 'maplibre-gl'
import StylesControl from './StylesControl'
import GeneralInfo from '../GeneralInfo'
import {SEVERITIES} from '../../constants'
import Copyright from '../Copyright'
import 'maplibre-gl/dist/maplibre-gl.css'
import {CustomPin} from './Pin'
import useWindowSize from '../../hooks/useWindowSize'
import {useLocation} from 'react-router-dom'
import {heatmapLayer} from './heatMapLayer'
import {ThemeContext} from '../../App'
import ModalVisMarkerInfo from '../modals/map/ModalVisMarkerinfo'
import CommunityMapButton from "../buttons/CommunityMapButton";
import LayerMapButton from "../buttons/LayerMapButton";
import * as lod from "lodash" ;
import {msg} from "@lingui/macro";
import {useLingui} from "@lingui/react";
import StyledPopup from "./StyledPopup";

const CENTER = [
    Number.parseFloat(process.env.REACT_APP_CENTER_LON)
    , Number.parseFloat(process.env.REACT_APP_CENTER_LAT)
] // LON, LAT Rome - This should be capital of the state
const ZOOM = 10
const PINSCALE = 0.75
const OFFSET = [0, -10]
const SPEED = 0.6

// Show cross on selecting position
const NewCrashSelector = ({selectorTop, selectorLeft}) => {
    const windowSize = useWindowSize()

    const HLine = styled(Box)(({theme}) => ({
        width: windowSize.width - theme.mixins.drawerWidth
        , height: 1
        , top: selectorTop
        , position: 'absolute'
        , backgroundColor: '#000'
        , pointerEvents: 'none'
        , zIndex: theme.zIndex.drawer - 1
    }))

    const VLine = styled(Box)(({theme}) => ({
        width: 1
        , height: windowSize.height
        , left: selectorLeft
        , position: 'absolute'
        , backgroundColor: '#000'
        , pointerEvents: 'none'
        , zIndex: theme.zIndex.drawer - 1
    }))

    return (
        <>
            <HLine/>
            <VLine/>
        </>
    )
}

export default function MapLibre({
                                     isMobile
                                     , isPositioning
                                     , isFormOpen
                                     , isHistoryOpen
                                     , showCrashes
                                     , showPublicCrashes
                                     , allCrashes
                                     , publicCrashes
                                     , crashesSummary
                                     , heatMapData
                                     , newCrash
                                     , getCrash
                                     , layersVisibility
                                     , handlePositioningState
                                     , handleIsPositionSetState
                                     , handleCoordinates
                                     , handleFormOpen
                                     , handleSetNewCrash
                                     , handleCrashToEdit
                                     , handleOwnPosCoords
                                     , showSafetyIssues
                                     , safetyIssuesList
                                     , layersList
                                     , setLayersList
                                     , communitySwitches
                                     , setCommunitySwitches
    ,isPositionSet
                                 }) {
    const mapKey = process.env.REACT_APP_API_KEY
    const theme = useTheme()
    const barHeight = theme.mixins.toolbar.minHeight
    const formDrawerWidth = theme.mixins.formDrawerWidth
    const location = useLocation()
    const {mode, toggleTheme} = useContext(ThemeContext)
    const state = location.state
    const {init} = state
    const user = useMemo(() => init?.user, [init])
    const organizations = useMemo(() => init?.organizations, [init])
    const [mapStyle, setMapStyle] = useState('openstreetmap')
    const geoControlRef = useRef()
    const mapRef = useRef();
    const {_} = useLingui()

    const [selectorTop, setSelectorTop] = useState(0)
    const [selectorLeft, setSelectorLeft] = useState(0)
    const transitionDuration = {
        enter: theme.transitions.duration.enteringScreen
        , exit: theme.transitions.duration.leavingScreen
    }
    const [popupInfo, setPopupInfo] = useState(null)
    const [popupPublicCrashInfo, setPopupPublicCrashInfo] = useState(null)
    const [popupSafetyIssueInfo, setPopupSafetyIssueInfo] = useState(null)
    const [modalinfo, setModalInfo] = useState(null)
    const [layerPopupInfo, setLayerPopupInfo] = useState(null);

    const handleSetStyle = style => setMapStyle(style)

    const clearPopups = () => {
        setPopupInfo(null)
        setPopupPublicCrashInfo(null)
        setPopupSafetyIssueInfo(null)
        setLayerPopupInfo(null)
    }

    const getGeoJSONCenter = (features) => {
        let coords = [];
        // Estrai tutte le coordinate da ogni feature
        features.forEach(feature => {
            const geom = feature.geometry;
            if (!geom) return;
            const type = geom.type;
            const coordinates = geom.coordinates;

            function extractCoords(coord) {
                if (typeof coord[0] === "number") {
                    coords.push(coord); // è un punto [lon, lat]
                } else {
                    coord.forEach(extractCoords);
                }
            }

            extractCoords(coordinates);
        });
        if (coords.length === 0) {
            return null;
        }
        // Calcola la media delle longitudini e latitudini
        let sumLon = 0, sumLat = 0;
        coords.forEach(c => {
            sumLon += c[0];
            sumLat += c[1];
        });
        return [sumLon / coords.length, sumLat / coords.length]; // [lon, lat]
    }

    useEffect(() => {
        clearPopups()
    }, [showCrashes, showPublicCrashes, showSafetyIssues])

    useEffect(() => {
        if (!mapRef) return;
        if (!mapRef.current) return;
        const focusedLayer = layersList.find(l => l.isFocused)
        if (!focusedLayer) return
        if (!focusedLayer.fileContent) return
        const mapAttribute = focusedLayer.mapAttribute || '';
        const hasAttribute = mapAttribute !== '';
        console.log('focusedLayer: ', focusedLayer.fileContent);
        // const coords = hasAttribute ? focusedLayer.fileContent.features[0].geometry.coordinates[0][0][0] : [0,0];
        const coords = getGeoJSONCenter(focusedLayer.fileContent.features)
        mapRef.current.flyTo({
            center: coords,
            zoom: 12,
            speed: 2,
            curve: 1,
            easing(t) {
                return t;
            }
        })
    }, [layersList])

    const allCrashesMarkers = useMemo(
        () =>
            allCrashes?.reduced?.map(crash => {
                const fill = SEVERITIES()[crash['Crash severity']]?.fill || SEVERITIES()[0].fill
                return (
                    <Marker
                        key={`marker-${crash['Crash id']}`}
                        longitude={crash.Longitude}
                        latitude={crash.Latitude}
                        offset={OFFSET}
                        onClick={e => {
                            /* If we let the click event propagates to the map, it will immediately close the popup
                            with `closeOnClick: true` */
                            e.originalEvent.stopPropagation()
                            setPopupInfo(crash)
                        }}
                    >
                        <CustomPin fill={fill} scale={PINSCALE} organization={!crash.merge_date && crash.organization}/>
                    </Marker>
                )
            }),
        [allCrashes]
    )

    const publicCrashesMarkers = useMemo(
        () =>
            publicCrashes?.map((crash, i) => {
                const fill = '#7C96FD'
                const longitude = +crash.longitude
                const latitude = +crash.latitude
                return (
                    <Marker
                        key={`public-crash-${i}`}
                        longitude={longitude}
                        latitude={latitude}
                        offset={OFFSET}
                        onClick={e => {
                            /* If we let the click event propagates to the map, it will immediately close the popup
                            with `closeOnClick: true` */
                            e.originalEvent.stopPropagation()
                            setPopupPublicCrashInfo({
                                Longitude: longitude
                                , Latitude: latitude
                                , ...crash
                            })
                        }}
                    >
                        <CustomPin fill={fill} scale={PINSCALE} organization={2}/>
                    </Marker>
                )
            }),
        [publicCrashes]
    )

    const safetyIssuesMarkers = useMemo(
        () =>
            safetyIssuesList?.map((crash, i) => {
                const fill = '#470080'
                const longitude = crash.longitude
                const latitude = crash.latitude
                return (
                    <Marker
                        key={`safety-issue-${i}`}
                        longitude={longitude}
                        latitude={latitude}
                        offset={OFFSET}
                        onClick={e => {
                            e.originalEvent.stopPropagation()
                            setPopupSafetyIssueInfo({
                                Longitude: longitude
                                , Latitude: latitude
                                , ...crash
                            })
                        }}
                    >
                        <CustomPin fill={fill} scale={PINSCALE} organization={crash.sender}/>
                    </Marker>
                )
            }),
        [safetyIssuesList]
    )

    const CrashPopup = ({popupInfo}) => {
        const hasContentPermission = (user, popupInfo, isMobile) => {
            // Admin users always have access
            if (user.type === 1) return true

            // Organization users need to match org and not be on mobile
            if (user.type === 2 &&
                popupInfo.organization === user.organization &&
                !isMobile) {
                return true
            }

            // Content owners always have access
            if (popupInfo.user_id === user.id) return true

            // if (user.type === 0 && user.basic_organization === -1) return true

            // Default deny
            return false
        }

        return (
            <PopupComponent
                offset={OFFSET.map(v => v * 1.5)}
                longitude={Number(popupInfo.Longitude)}
                latitude={Number(popupInfo.Latitude)}
                onClose={() => setPopupInfo(null)}
            >
                <Box display='flex' flexDirection='column' alignItems='center'>
                    <Typography sx={{fontSize: 15}}>Report type:</Typography>
                    <Typography fontWeight='bold'
                                sx={{fontSize: 14}}>{popupInfo.merge_date ? 'Merged/Validated' : organizations?.find(org => org?.id === popupInfo?.organization)?.description}</Typography>
                    <br/>
                    <Typography sx={{fontSize: 15}}>Crash id:</Typography>
                    <Typography fontWeight='bold' sx={{fontSize: 14}}>{popupInfo['Crash id']}</Typography>
                    {/* {!isEditing && (user.type === 1 || */}
                    {hasContentPermission(user, popupInfo, isMobile) &&
                        <Button
                            variant='contained'
                            size='small'
                            sx={{
                                mt: 1,
                                border: mode === 'light' ? '1px solid white' : '1px solid black',
                                color: theme.palette.newColors.gray,
                                background: theme.palette.newColors.lightGray
                            }}
                            onClick={() => {
                                if (popupInfo.merge_date) {
                                    // console.log('CLICK', popupInfo['Crash id'], popupInfo.organization)
                                    getCrash(popupInfo['Crash id'], popupInfo.organization)
                                } else {
                                    // console.log('CLICK', popupInfo['Crash id'], popupInfo.organization, popupInfo.user_id)
                                    getCrash(popupInfo['Crash id'], popupInfo.organization, popupInfo.user_id)
                                }
                                handleIsPositionSetState(true)
                            }}
                        >
                            {popupInfo.merge_date ? 'VIEW' : 'EDIT'}
                        </Button>
                    }
                </Box>
            </PopupComponent>
        )
    }

    const PublicCrashPopup = ({popupInfo}) => (
        <PopupComponent
            offset={OFFSET.map(v => v * 1.5)}
            longitude={Number(popupInfo.Longitude)}
            latitude={Number(popupInfo.Latitude)}
            onClose={() => {
                setPopupInfo(null);
                setLayerPopupInfo(null);
            }}
        >
            <Box display='flex' flexDirection='column' alignItems='center'>
                <Typography variant='body2'>Report type: Public</Typography>
                <br/>
                <Typography variant='body2'>Crash id:</Typography>
                <Typography fontWeight='bold' variant='body1'>{popupInfo.id}</Typography>
                <Button sx={{textDecoration: 'underline', marginTop: 2, cursor: 'pointer', color: 'white'}}
                        onClick={() => {
                            setModalInfo({data: popupInfo, label: 'crash details'})
                        }}>View</Button>
            </Box>
        </PopupComponent>
    )

    const SafetyIssuePopup = ({popupInfo}) => {
        return (
            <PopupComponent
                offset={OFFSET.map(v => v * 1.5)}
                longitude={Number(popupInfo.Longitude)}
                latitude={Number(popupInfo.Latitude)}
                onClose={() => {
                    setPopupInfo(null);
                    setLayerPopupInfo(null);
                }}
            >
                <Box display='flex' flexDirection='column' alignItems='center'>
                    <Typography sx={{fontSize: 15}}>Report type: </Typography>
                    <Typography fontWeight='bold' sx={{fontSize: 14}}>Safety Issue</Typography>

                    <Typography sx={{marginTop: 2, fontSize: 15}} variant='body2'>Segnalation id:</Typography>
                    <Typography fontWeight='bold' sx={{fontSize: 14}}>{popupInfo.id}</Typography>
                    <Typography sx={{marginTop: 2, fontSize: 15}}>Description:</Typography>
                    <Typography fontWeight='bold'
                                sx={{textAlign: 'center', fontSize: 14}}>{popupInfo.description}</Typography>
                    <Button sx={{textDecoration: 'underline', marginTop: 2, cursor: 'pointer', color: 'white'}}
                            onClick={() => {
                                setModalInfo({data: popupInfo, label: 'report details'})
                            }}>View</Button>
                </Box>
            </PopupComponent>
        )
    }

    const CrashMarker = ({crash, handleCoordinates}) => {
        const popupHTML = `
            <div style="display: flex; flex-direction: column; align-items: center">
              <p style="font-size: 0.875rem; font-family: 'Roboto', sans-serif; margin: 0; line-height: 1.43; letter-spacing: 0.01071em;">Report type:</p>
              <h5 style="font-size: 1.2rem; font-family: 'Roboto', sans-serif; font-weight: 400; margin: 0; line-height: 1.334; letter-spacing: 0em;">
                ${crash.merge_date ? 'Merged/Validated' : organizations?.find(org => org?.id === crash?.organization)?.description}
              </h5>
              <br />
              <p style="font-size: 0.875rem; font-family: 'Roboto', sans-serif; margin: 0; line-height: 1.43; letter-spacing: 0.01071em;">Crash id:</p>
              <p style="font-size: 1rem; font-family: 'Roboto', sans-serif; font-weight: 700; margin: 0; line-height: 1.5; letter-spacing: 0.00938em;">${crash['Crash id']}</p>
            </div>`
        const {current: map} = useMap()
        const fill = SEVERITIES()[crash['Crash severity'] ?? 0].fill
        if (crash.Latitude && crash.Longitude) {
            map.flyTo({center: {lat: crash.Latitude, lng: crash.Longitude}, speed: SPEED})
        }
        // console.log(allCrashes)
        if (allCrashes?.reduced?.length && allCrashes?.reduced?.find(acr => acr['Crash id'] === crash['Crash id'])) {
            setPopupInfo(null);
            setLayerPopupInfo(null);
            handleCrashToEdit(allCrashes, crash['Crash id'])
        }
        return (
            <Marker
                longitude={crash.Longitude}
                latitude={crash.Latitude}
                offset={OFFSET}
                draggable
                onDragEnd={e => handleCoordinates(e.lngLat)}
                popup={new Popup({offset: OFFSET.map(v => v * 1.5)})
                    .setHTML(popupHTML)
                }
            >
                <CustomPin
                    fill={fill}
                    scale={PINSCALE}
                    organization={!crash.merge_date && crash.organization}
                />
            </Marker>
        )
    }

    const onClick = useCallback(e => {
        // console.log('LNGLAT', e.lngLat, 'EEE', e)
        handleSetNewCrash({isNew: true, Longitude: e.lngLat.lng, Latitude: e.lngLat.lat})
        handleCoordinates(e.lngLat)
        handleFormOpen(true)
        handlePositioningState(false)
        handleIsPositionSetState(true)
        // map.off('click', onclick) // Important: remove the listener
    }, [handlePositioningState, handleCoordinates, handleFormOpen, handleSetNewCrash, handleIsPositionSetState])

    const MapLayer = ({layer, k}) => {
        const paletteItems = [
            {name: 'red', colors: ['#DDB100', '#DD8500', '#DD5800', '#DD2C00', '#DD0000']},
            {name: 'blue', colors: ['#00A0CB', '#0079B8', '#0051A6', '#002A93', '#000280']},
            {name: 'gray', colors: ['#BBBEBF', '#959798', '#6F7171', '#494A4A', '#232323']}
        ];
        const sourceID = 'layers-source-' + layer.id;
        const [infoMarkerOpen, setInfoMarkerOpen] = useState(true);

        const paletteName = layer.palette;
        const palette = paletteItems.find(p => p.name === paletteName);
        const mapAttribute = layer.mapAttribute || '';
        const hasAttribute = palette && mapAttribute !== '';

        if (!layer.fileContent || !layer.fileContent.features) return <></>;

        const pointLayerJson = {
            id: 'layer-point-' + layer.id,
            type: 'circle',
            source: sourceID,
            layout: {
                // 'line-join': 'round',
                // 'line-cap': 'round'
            },
            paint: {
                'circle-color': palette.colors[4],
                'circle-radius': 6,

                // 'line-width': 3,
            },
        };

        const lineLayerJson = {
            id: 'layer-line-' + layer.id,
            type: 'line',
            source: sourceID,
            layout: {
                'line-join': 'round',
                'line-cap': 'round'
            },
            paint: {
                'line-color': palette.colors[2],
                'line-width': 3,
            },
        };

        const infoFeatures = lod.orderBy(layer.fileContent.features, feat => parseFloat(feat.properties[mapAttribute]), ['asc']);
        const features = lod.orderBy(layer.fileContent.features, feat => parseFloat(feat.properties[mapAttribute]), ['desc']);

        const MAX_VALUE = hasAttribute ? Math.max(...features.map(elem => parseFloat(elem.properties[mapAttribute]))) : 0;
        const MIN_WIDTH = 7;
        const WIDTH_STEP = 1;
        const layerCoord = getGeoJSONCenter(features)
        let infoCoord;

        if (hasAttribute) {
            infoCoord = getGeoJSONCenter(features)
        }

        const getMarkerColor = (value) => {
            const val = value / MAX_VALUE;
            if (val === 0) return palette.colors[0];
            if (val <= 0.25) return palette.colors[1];
            if (val <= 0.50) return palette.colors[2];
            if (val <= 0.75) return palette.colors[3];
            if (val <= 1) return palette.colors[4];
        }

        const InfoMarker = () => {
            return (
                <Marker
                    key={`layer-marker-layer-${layer.id}`}
                    longitude={infoCoord[0]}
                    latitude={infoCoord[1]}
                    offset={OFFSET}
                    onClick={e => {
                        /* If we let the click event propagates to the map, it will immediately close the popup
                        with `closeOnClick: true` */
                        e.originalEvent.stopPropagation()
                        setInfoMarkerOpen(!infoMarkerOpen)
                    }}
                >
                    <CustomPin fill={palette.colors[4]} scale={PINSCALE} organization={0}/>
                </Marker>
            )
        }

        const getLineLayerWithMapAttributeJson = () => {
            return {
                id: 'layer-' + layer.id,
                type: 'line',
                source: sourceID,
                layout: {
                    'line-join': 'round',
                    'line-cap': 'round'
                },
                paint: {
                    'line-color': [
                        'interpolate',
                        ['linear'],
                        // red is higher when feature.properties.temperature is higher
                        ['to-number', ['get', mapAttribute]],
                        0, palette.colors[0],
                        MAX_VALUE * 0.25, palette.colors[1],
                        MAX_VALUE * 0.50, palette.colors[2],
                        MAX_VALUE * 0.75, palette.colors[3],
                        MAX_VALUE, palette.colors[4]
                    ],
                    'line-width': [
                        'interpolate',
                        ['linear'],
                        // red is higher when feature.properties.temperature is higher
                        ['to-number', ['get', mapAttribute]],
                        0, MIN_WIDTH,
                        MAX_VALUE * 0.25, MIN_WIDTH + WIDTH_STEP,
                        MAX_VALUE * 0.50, MIN_WIDTH + WIDTH_STEP * 2,
                        MAX_VALUE * 0.75, MIN_WIDTH + WIDTH_STEP * 3,
                        MAX_VALUE, MIN_WIDTH + WIDTH_STEP * 4,
                    ],
                },
            };
        }


        return (
            <>
                {!hasAttribute ? (
                    <>
                        <Source
                            key={'key-line-' + sourceID}
                            id={`line-source-${layer.id}`}
                            type={'geojson'}
                            data={layer.fileContent}
                            lineMetrics={true}
                        >
                            <Layer {...lineLayerJson} />
                        </Source>
                        <Source
                            key={'key-point-' + sourceID}
                            id={`point-source-${layer.id}`}
                            type={'geojson'}
                            data={layer.fileContent}
                            lineMetrics={true}
                        >
                            <Layer {...pointLayerJson} />
                        </Source>

                    </>
                ) : (
                    <Source
                        key={'key-' + sourceID}
                        id={sourceID}
                        type={'geojson'}
                        data={layer.fileContent}
                        lineMetrics={true}
                    >
                        {hasAttribute && <Layer {...getLineLayerWithMapAttributeJson()} />}
                        {hasAttribute && <InfoMarker/>}
                    </Source>
                )}

                {
                    hasAttribute && infoMarkerOpen && (
                        <PopupComponent
                            offset={OFFSET.map(v => v * 1.5)}
                            longitude={infoCoord[0]}
                            latitude={infoCoord[1]}
                            onClose={() => setInfoMarkerOpen(false)}
                        >
                            <Box display='flex' flexDirection='column' alignItems='center'>
                                {
                                    infoFeatures.map((elem, index) => {
                                        const value = parseFloat(elem.properties[mapAttribute]);
                                        const color = getMarkerColor(value);
                                        return (
                                            <Stack direction={'row'} spacing={1}>
                                                <Box
                                                    sx={{
                                                        height: '1vh',
                                                        width: '1vh',
                                                        backgroundColor: color,
                                                    }}
                                                />
                                                <Typography variant={'h5'}>{mapAttribute}: </Typography>
                                                <Typography variant={'body1'}>{value} </Typography>
                                            </Stack>
                                        )

                                    })
                                }
                            </Box>
                        </PopupComponent>
                    )

                }

            </>
        )
    }

    return (
        <Box
            height={isMobile ? `calc(100vh - ${barHeight * 2}px)` : `calc(100vh - ${barHeight}px)`}
            ml={!isMobile && isFormOpen ? `${formDrawerWidth}px` : 0}
            mr={!isMobile && isHistoryOpen ? `${formDrawerWidth}px` : 0}
        >
            {/* <RenderCounter name="MapLibre" /> */}
            <Map
                /* initialViewState={{ viewport }} */
                style={{width: '100%', height: '100%', cursor: isPositioning ? 'none' : 'grab'}}
                mapStyle={`https://api.maptiler.com/maps/${mapStyle}/style.json?key=${mapKey}`}
                attributionControl={false}
                onLoad={() => {
                    // Activate as soon as the map is loaded
                    geoControlRef.current?.trigger()
                }}
                onMouseMove={e => {
                    if (!isMobile && isPositioning) {
                        setSelectorTop(e.point.y)
                        setSelectorLeft(isFormOpen ? e.point.x - formDrawerWidth : e.point.x)
                    }
                }}
                cursor={!isMobile && isPositioning ? 'none' : 'Grab'}
                onClick={e => {
                    if (isPositioning) onClick(e)
                }}
                ref={mapRef}
            >
                <CommunityMapButton setCommunitySwitches={setCommunitySwitches} communitySwitches={communitySwitches}/>
                <LayerMapButton isPositionSet={isPositionSet} isPositioning={isPositioning} isFormOpen={isFormOpen} setLayersList={setLayersList}
                                layersList={layersList}/>
                <NavigationControl position='bottom-right'/>
                <GeolocateControl

                    ref={geoControlRef}
                    position='bottom-right'
                    // trackUserLocation={isMobile} // Causes re-renders on mobile!!!!
                    showAccuracyCircle={isMobile}
                    positionOptions={{enableHighAccuracy: true}}
                    onGeolocate={({coords: {latitude, longitude}}) => {
                        handleOwnPosCoords({lat: latitude, lng: longitude})
                    }}
                />
                <ScaleControl />
                <StylesControl handleSetStyle={handleSetStyle}
                               position='bottom-right'/>
                {/**
                 {!isMobile &&
                 <Box sx={{ marginTop: '10px', marginRight: '10px', float: 'right' }}>
                 <UserControls state={state} />
                 </Box>
                 }
                 */}
                <Copyright bgcolor='hsla(0, 0%, 100%, .5)' left='50%' bottom={0}/>
                <Slide
                    in={isPositioning}
                    timeout={transitionDuration}
                    unmountOnExit
                >
                    <Box
                        position='absolute'
                        top={isMobile ? '1rem' : '3rem'}
                        width='100%'
                        display='flex'
                        flexDirection='column'
                        alignItems='center'
                        zIndex={isMobile ? theme.zIndex.drawer - 1 : 99999}
                    >
                        <Chip label={_(msg`Please select the new crash location`)} color='error'/>
                    </Box>
                </Slide>
                {isPositioning && <NewCrashSelector selectorTop={selectorTop} selectorLeft={selectorLeft}/>}
                {newCrash && <CrashMarker crash={newCrash} handleCoordinates={handleCoordinates}/>}
                {showCrashes && allCrashesMarkers}
                {showPublicCrashes && publicCrashesMarkers}
                {showSafetyIssues && safetyIssuesMarkers}
                {popupInfo && <CrashPopup popupInfo={popupInfo}/>}
                {popupPublicCrashInfo && <PublicCrashPopup popupInfo={popupPublicCrashInfo}/>}
                {popupSafetyIssueInfo && <SafetyIssuePopup popupInfo={popupSafetyIssueInfo}/>}
                {modalinfo && <ModalVisMarkerInfo data={modalinfo.data} type={modalinfo.label} isMobile={isMobile}
                                                  setData={setModalInfo}/>}
                <GeneralInfo crashesSummary={crashesSummary}/>
                {heatMapData && (
                    <Source key='heatmap' type='geojson' data={heatMapData}>
                        <Layer
                            key='heatmap'
                            {...heatmapLayer}
                            layout={{visibility: layersVisibility?.heatmap}}
                        />
                    </Source>
                )}
                {layersList && layersList.filter(l => l.isOpen).map((layer, k) => {
                    return (
                        <MapLayer key={k} layer={layer}/>
                    )
                })}
            </Map>
        </Box>
    )
}
